import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import endpoints from "../../endpoint";

// Async thunk to fetch assigned leads
export const fetchAssignedLeads = createAsyncThunk(
  "leads/fetchAssignedLeads",
  async (_, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(
        `${endpoints.serverBaseUrl}api/lead/getassignedleads`,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch leads");
    }
  }
);

// Async thunk to update lead status
export const updateLeadStatus = createAsyncThunk(
  "leads/updateLeadStatus",
  async ({ id, newStatus, remark }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("token");
      await axios.put(
        `${endpoints.serverBaseUrl}api/lead/updatelead/${id}`,
        { status: newStatus, remark },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      return { id, newStatus };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update status");
    }
  }
);

// Async thunk to close a lead with amount & payment proof
export const closeLead = createAsyncThunk(
  "leads/closeLead",
  async ({ id, amountReceived, paymentProof }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("token");

      // Step 1: Request a pre-signed URL from backend
      const presignedResponse = await axios.post(
        `${endpoints.serverBaseUrl}api/generate-presigned-url`,
        { fileName: paymentProof.name, fileType: paymentProof.type },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );

      const { presignedUrl, publicId, cloudflareBaseUrl } =
        presignedResponse.data;
      const fileUrl = `${cloudflareBaseUrl}/${publicId}`;

      // Step 2: Upload file to Cloudflare
      await axios.put(presignedUrl, paymentProof, {
        headers: { "Content-Type": paymentProof.type },
      });

      // Step 3: Send the Cloudflare file URL to update the lead
      await axios.put(
        `${endpoints.serverBaseUrl}api/lead/updatelead/${id}`,
        {
          status: "Closed",
          amountReceived,
          paymentProof: fileUrl,
          publicId,
        },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );

      return { id, amountReceived, paymentProof: fileUrl };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to close lead");
    }
  }
);

const leadSlice = createSlice({
  name: "leads",
  initialState: {
    leads: [],
    leadsCount: 0,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignedLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignedLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload;
        state.leadsCount = action.payload.length;
      })

      .addCase(fetchAssignedLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        const { id, newStatus } = action.payload;
        const lead = state.leads.find((lead) => lead._id === id);
        if (lead) {
          lead.status = newStatus;
        }
      })
      .addCase(closeLead.fulfilled, (state, action) => {
        const { id, amountReceived, paymentProof } = action.payload;
        const lead = state.leads.find((lead) => lead._id === id);
        if (lead) {
          lead.status = "Closed";
          lead.amountReceived = amountReceived;
          lead.paymentProof = paymentProof;
        }
      });
  },
});

export default leadSlice.reducer;
